import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { getSiteProfile } from "../lib/site-profile";
import { 
  Crown, Check, Sparkles, Zap, Shield, Star, Heart,
  Waves, Unlock, Gift, TrendingUp
} from "lucide-react";

const PremiumPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const siteProfile = getSiteProfile();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API}/subscriptions/plans`);
      setPlans(response.data.plans || []);
    } catch (error) {
      console.error("Fetch plans error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (planId) => {
    if (!user) {
      navigate("/auth?redirect=/premium");
      return;
    }

    setProcessing(true);
    try {
      const response = await axios.post(`${API}/payments/checkout`, {
        payment_type: planId,
        origin_url: window.location.origin
      }, { withCredentials: true });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error("Checkout error:", error);
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to start checkout. Check PayPal or Stripe keys on the API.");
    } finally {
      setProcessing(false);
    }
  };

  const isPro = user?.tier === "pro" || user?.subscription_tier === "premium" || user?.is_pro;

  const userPlans = plans.filter(p => p.plan_type === "user" && p.plan_id !== "user_basic");

  const features = [
    { icon: Unlock, title: "Unlimited Discovery", desc: "Access all spots without daily limits" },
    { icon: Zap, title: "Instant Booking", desc: "Book experiences with immediate confirmation" },
    { icon: Gift, title: "10% Discount", desc: "Save on every booking you make" },
    { icon: Heart, title: "Save Favorites", desc: "Build unlimited lists and itineraries" },
    { icon: Shield, title: "No Ads", desc: "Enjoy a clean, ad-free experience" },
    { icon: Sparkles, title: "AI Concierge", desc: "Personalized recommendations just for you" },
    { icon: TrendingUp, title: "Priority Support", desc: "Get help when you need it most" },
    { icon: Star, title: "Hidden Gems", desc: "Access exclusive PRO-only content" },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-amber-900/50">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-slate-900/80 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-orange-500 flex items-center justify-center">
                <Waves className="w-5 h-5 text-white" />
              </div>
              <span className="font-serif font-bold text-lg text-white">{siteProfile.brandName}</span>
            </Link>
            <Link to="/" className="text-white/70 hover:text-white transition-colors">
              Back to Home
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative py-20 px-4 md:px-8 text-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-500/20 via-orange-500/10 to-transparent" />
        <div className="absolute top-20 left-1/2 -translate-x-1/2 w-96 h-96 bg-amber-500/30 rounded-full blur-3xl" />
        
        <div className="relative z-10 max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/20 border border-amber-500/30 text-amber-400 text-sm font-medium mb-8">
            <Sparkles className="w-4 h-4" />
            {isPro ? "You're a PRO member!" : "Limited Time: 7-Day Free Trial"}
          </div>
          
          <h1 className="text-4xl md:text-6xl font-serif font-bold text-white mb-6">
            Unlock the Full
            <span className="block bg-gradient-to-r from-amber-400 via-orange-400 to-rose-400 bg-clip-text text-transparent">
              Caribbean Experience
            </span>
          </h1>
          <p className="text-xl text-white/70 max-w-2xl mx-auto">
            PRO members get unlimited access, instant bookings, exclusive content, and personalized AI recommendations.
          </p>
        </div>
      </section>

      {/* Already PRO */}
      {isPro && (
        <section className="px-4 md:px-8 pb-12">
          <div className="max-w-3xl mx-auto p-8 rounded-3xl bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 text-center">
            <Crown className="w-16 h-16 mx-auto mb-4 text-amber-400" />
            <h2 className="text-2xl font-bold text-white mb-2">You're Already PRO!</h2>
            <p className="text-white/70 mb-6">Thank you for being a premium member. Enjoy all the exclusive benefits.</p>
            <Link to="/dashboard">
              <Button className="rounded-full bg-white text-amber-600 hover:bg-gray-100">
                Go to Dashboard
              </Button>
            </Link>
          </div>
        </section>
      )}

      {/* Pricing Cards */}
      {!isPro && (
        <section className="px-4 md:px-8 pb-20">
          <div className="max-w-5xl mx-auto">
            {loading ? (
              <div className="grid md:grid-cols-3 gap-6">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="h-96 rounded-3xl bg-white/5 animate-pulse" />
                ))}
              </div>
            ) : (
              <div className="grid md:grid-cols-3 gap-6">
                {userPlans.map((plan) => {
                  const isPopular = plan.badge === "Most Popular";
                  
                  return (
                    <div 
                      key={plan.plan_id}
                      className={`relative flex flex-col rounded-3xl p-1 ${
                        isPopular 
                          ? "bg-gradient-to-b from-amber-400 via-orange-500 to-rose-500" 
                          : "bg-white/10"
                      }`}
                    >
                      {plan.badge && (
                        <div className={`absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-sm font-semibold ${
                          isPopular ? "bg-white text-orange-600" : "bg-cyan-500 text-white"
                        }`}>
                          {plan.badge}
                        </div>
                      )}
                      
                      <div className={`flex-1 flex flex-col rounded-[1.4rem] p-6 md:p-8 ${
                        isPopular ? "bg-slate-900" : "bg-slate-800/50"
                      }`}>
                        <h3 className="text-xl font-bold text-white mb-2">{plan.name}</h3>
                        <div className="flex items-baseline gap-1 mb-6">
                          <span className="text-4xl font-bold text-white">${plan.price}</span>
                          <span className="text-white/60">/{plan.interval}</span>
                        </div>

                        <ul className="flex-1 space-y-3 mb-8">
                          {plan.features?.map((feature, i) => (
                            <li key={i} className="flex items-start gap-3 text-white/80">
                              <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>

                        <Button 
                          onClick={() => handleSubscribe(plan.plan_id)}
                          disabled={processing}
                          className={`w-full rounded-full py-6 font-semibold transition-all ${
                            isPopular 
                              ? "bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:shadow-xl hover:shadow-orange-500/25" 
                              : "bg-white text-slate-900 hover:bg-gray-100"
                          }`}
                        >
                          {processing ? "Processing..." : "Start Free Trial"}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Features Grid */}
      <section className="px-4 md:px-8 pb-20">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-serif font-bold text-white text-center mb-12">
            Everything You Get with PRO
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, idx) => {
              const Icon = feature.icon;
              return (
                <div key={idx} className="p-6 rounded-2xl bg-white/5 border border-white/10 hover:border-white/20 transition-colors">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500/20 to-orange-500/20 flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-amber-400" />
                  </div>
                  <h3 className="font-semibold text-white mb-2">{feature.title}</h3>
                  <p className="text-white/60 text-sm">{feature.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="px-4 md:px-8 pb-20">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-serif font-bold text-white text-center mb-12">
            Loved by Travelers
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { name: "Sarah M.", location: "New York", text: "Found the most amazing hidden beach thanks to PRO recommendations. Worth every penny!" },
              { name: "James T.", location: "London", text: "The instant booking feature saved me so much time. No more waiting for confirmations." },
              { name: "Maria L.", location: "Toronto", text: "The 10% discount paid for my subscription in just one trip. Absolutely recommend!" },
            ].map((testimonial, idx) => (
              <div key={idx} className="p-6 rounded-2xl bg-white/5 border border-white/10">
                <div className="flex items-center gap-1 text-amber-400 mb-4">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-current" />)}
                </div>
                <p className="text-white/80 mb-4">"{testimonial.text}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-orange-500 flex items-center justify-center text-white font-semibold">
                    {testimonial.name[0]}
                  </div>
                  <div>
                    <div className="text-white font-medium">{testimonial.name}</div>
                    <div className="text-white/50 text-sm">{testimonial.location}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="px-4 md:px-8 pb-20">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-serif font-bold text-white text-center mb-12">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {[
              { q: "Can I cancel anytime?", a: "Yes! You can cancel your subscription at any time. Your PRO benefits will continue until the end of your billing period." },
              { q: "What payment methods do you accept?", a: "We accept all major credit cards through our secure Stripe payment processing." },
              { q: "Is there really a free trial?", a: "Absolutely! Try PRO free for 7 days. You won't be charged until the trial ends, and you can cancel anytime." },
              { q: "What happens to my bookings if I cancel?", a: "Any bookings you've made will be honored. You just won't have access to PRO features for new bookings." },
            ].map((faq, idx) => (
              <div key={idx} className="p-6 rounded-2xl bg-white/5 border border-white/10">
                <h3 className="font-semibold text-white mb-2">{faq.q}</h3>
                <p className="text-white/60">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      {!isPro && (
        <section className="px-4 md:px-8 pb-20">
          <div className="max-w-3xl mx-auto text-center p-12 rounded-3xl bg-gradient-to-br from-amber-500 to-rose-500">
            <Crown className="w-16 h-16 mx-auto mb-6 text-white" />
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-4">
              Ready to Go PRO?
            </h2>
            <p className="text-white/90 text-lg mb-8">
              Join thousands of travelers who've upgraded their Caribbean experience.
            </p>
            <Button 
              onClick={() => handleSubscribe("user_pro_monthly")}
              disabled={processing}
              className="px-12 py-6 rounded-full bg-white text-orange-600 font-bold text-lg hover:bg-gray-50 shadow-xl"
            >
              Start Your Free Trial
            </Button>
            <p className="mt-4 text-white/70 text-sm">No credit card required • Cancel anytime</p>
          </div>
        </section>
      )}
    </div>
  );
};

export default PremiumPage;
